import TeamPerformance, { TeamMatches } from "./TeamPerformance";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Alert } from "@mui/material";
import {
  teamDecisionEvidence,
  decisionLabels,
  decisionOrigins,
  decisionField,
  decisionRowValue,
  explainTeamDecision,
} from "../helpers/teamDecision";
export default function TeamDecisionReview({
  data,
  teamId,
  model,
  metric,
  level = "summary",
  side = "for",
  venue,
  recent,
  en,
  intent,
  onLevel,
}) {
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
          })
        : "—";
  const evidence = useMemo(
    () => teamDecisionEvidence(data, teamId, { model, metric, venue, recent }),
    [data, teamId, model, metric, venue, recent]
  );
  const [explanation, setExplanation] = useState(null),
    output = useRef(null);
  const field = decisionField(level, side, venue, metric),
    label = (k) => decisionLabels[k][en ? 1 : 0];
  const name =
    level === "summary"
      ? metric === "total_strength"
        ? t("Torquotient", "Goal ratio")
        : t("Toreschnitt", "Goals/game")
      : t(
          side === "for" ? "Für pro Spiel" : "Gegen pro Spiel",
          side === "for" ? "For per game" : "Against per game"
        );
  useEffect(() => setExplanation(null), [evidence, level, side, en]);
  function explain(overview = false) {
    setExplanation(
      explainTeamDecision(evidence, { en, level, side, overview })
    );
  }
  useEffect(() => {
    if (intent?.count) explain(true);
  }, [intent]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (explanation)
      output.current?.scrollIntoView?.({
        block: "nearest",
        behavior: "smooth",
      });
  }, [explanation]);
  return (
    <section
      className="ta-decision"
      aria-label={t("Fokussierter Vergleich", "Focused comparison")}
    >
      <h2>▥ {t("Statistik & Einordnung", "Statistics & context")}</h2>
      <div className="ta-focus-heading">
        <b>{name}</b> ·{" "}
        {model === "result"
          ? t("Ergebnismodell", "Results model")
          : t("Statistikmodell", "Statistics model")}{" "}
        ·{" "}
        {venue === "all"
          ? t("Gesamt", "Overall")
          : venue === "home"
          ? t("Heim", "Home")
          : t("Auswärts", "Away")}
      </div>
      <div className="ta-models">
        {["result", "statistics"].map((key) => (
          <div className="ta-model" key={key}>
            <b>
              {key === "result"
                ? t("Ergebnismodell · gespeichert", "Results model · saved")
                : t(
                    "Statistikmodell · gespeichert",
                    "Statistics model · saved"
                  )}
            </b>
            <p className="ta-focus-value">
              {name}: {fmt(evidence.settings[key]?.[field])}
            </p>
          </div>
        ))}
      </div>
      <div className="ta-scroll">
        <table className="ta-decision-table">
          <thead>
            <tr>
              {[
                t("Grundlage", "Source"),
                name,
                t("Zeitraum", "Period"),
                t("Spiele / Abdeckung", "Matches / coverage"),
              ].map((x) => (
                <th key={x}>{x}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {evidence.rows.map((r) => (
              <tr key={r.key}>
                <th scope="row">
                  <details>
                    <summary>
                      <span
                        className={`ta-source-dot ${r.key}`}
                        aria-hidden="true"
                      />
                      {label(r.key)}
                      {r.key === "recent" ? ` · ${recent}` : ""}
                    </summary>
                    <p>{decisionOrigins[r.key][en ? 1 : 0]}</p>
                    <p>
                      {t(
                        "Torsummen · Für / Gegen",
                        "Goal totals · for / against"
                      )}
                      :{" "}
                      {["real", "recent", "previous"].includes(r.key)
                        ? [r.for, r.against]
                            .map((v) =>
                              Number.isFinite(v)
                                ? v.toLocaleString(en ? "en-GB" : "de-DE", {
                                    maximumFractionDigits: 0,
                                  })
                                : "—"
                            )
                            .join("−")
                        : [fmt(r.for), fmt(r.against)].join("−")}
                      .
                    </p>
                    <p>
                      {level === "summary"
                        ? metric === "total_strength"
                          ? t(
                              "Quotient = Summe Für / Summe Gegen.",
                              "Ratio = total for / total against."
                            )
                          : t(
                              "Toreschnitt = (Summe Für + Summe Gegen) / Spiele.",
                              "Goals/game = (total for + total against) / matches."
                            )
                        : t(
                            "Pro Spiel = Torsumme der gewählten Seite / berücksichtigte Spiele.",
                            "Per game = goal total for the selected side / included matches."
                          )}
                    </p>
                    {r.against === 0 && field === "total_strength" && (
                      <p>
                        {t(
                          "Keine Gegentore: Quotient nicht definiert.",
                          "No goals conceded: ratio undefined."
                        )}
                      </p>
                    )}
                    {r.missing.map((g) => (
                      <p key={g.id}>
                        {t("Fehlend", "Missing")}: {g.home} – {g.away} ·{" "}
                        {g.date?.slice(0, 10)}
                      </p>
                    ))}
                    {r.fixtures.length > 0 && (
                      <details>
                        <summary>
                          {t(
                            "Verwendete Spiele und Herkunft",
                            "Included matches and provenance"
                          )}
                        </summary>
                        <TeamMatches
                          games={(data.observations || []).filter((g) =>
                            r.fixtures.some((f) => f.id === g.id)
                          )}
                          {...{ teamId, en, model }}
                        />
                      </details>
                    )}
                  </details>
                </th>
                <td className="ta-focus-value">
                  {fmt(decisionRowValue(r, field))}
                </td>
                <td>{r.period || t("Nicht verfügbar", "Unavailable")}</td>
                <td>
                  {r.count}/{r.selected}
                  {!r.count
                    ? ` · ${t("Grundlage fehlt", "Missing evidence")}`
                    : r.count < r.selected
                    ? ` · ${t("unvollständig", "incomplete")}`
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ta-decision-caution">
        {t(
          "Ungerundete Torsummen; Unterschiede bei Gegnern, Spielmengen und Spielorten beachten. Gesamtsettings mitteln Heim/Auswärts gleich, historische Werte die tatsächlich gespielten Begegnungen.",
          "Unrounded goal totals; consider different opponents, samples and venues. Overall settings weight home/away equally; historical values weight actual matches."
        )}
      </p>
      <details>
        <summary>
          {t("Gegner und Spielauswahl", "Opponents and selected fixtures")}
        </summary>
        {evidence.schedule.map((g) => (
          <span key={g.id}>
            {g.opponent} (
            {g.venue === "home" ? t("Heim", "Home") : t("Auswärts", "Away")}) ·{" "}
          </span>
        ))}
      </details>
      <TeamPerformance {...{ data, teamId, model, recent, venue, en }} />
      <Button variant="contained" onClick={() => explain()}>
        {level === "summary"
          ? metric === "total_strength"
            ? t("Spielstärke erklären", "Explain strength")
            : t("Torniveau erklären", "Explain goal level")
          : t("Ausgewählte Werte erklären", "Explain selected values")}
      </Button>
      <div ref={output}>
        {explanation && (
          <div className="ta-decision-explanation">
            <h3>
              {t(
                "Einordnung aus vorhandenen Daten",
                "Context from existing data"
              )}
            </h3>
            <small>
              {t(
                "Regelbasierte Erklärung · keine externen KI-Aufrufe",
                "Rule-based explanation · no external AI calls"
              )}
            </small>
            {explanation.claims.map((c, i) => (
              <article key={i}>
                <h4>{c.title}</h4>
                <p>{c.text}</p>
              </article>
            ))}
            {explanation.missing.length > 0 && (
              <Alert severity="info">
                {t("Unvollständige Grundlagen", "Incomplete evidence")}:{" "}
                {explanation.missing.join(" · ")}
              </Alert>
            )}
            <details>
              <summary>
                {t("Grenzen der Einordnung", "Interpretation limits")}
              </summary>
              {explanation.cautions.map((s) => (
                <p key={s}>{s}</p>
              ))}
            </details>
            <p>{explanation.proposalReason}</p>
            <div className="ta-next-step">
              <Button onClick={() => onLevel?.("composition")}>
                {t("Für / Gegen prüfen", "Review For / Against")}
              </Button>
              <Button onClick={() => onLevel?.("venue")}>
                {t("Heim / Auswärts prüfen", "Review Home / Away")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
